import { Alert, Badge, Button, Col, Form, Row } from 'react-bootstrap';

export type ComplianceDocumentUploadRow = {
  key: string;
  label: string;
  description?: string;
  badgeLabel: 'Required' | 'Optional' | 'Conditional';
  helperText?: string;
  existingFileName: string | null;
  existingFileUrl: string | null;
  selectedFile: File | null;
  errorMessage?: string;
  accept?: string;
  onFileChange: (file: File | null) => void | Promise<void>;
  onRemoveExisting: () => void | Promise<void>;
};

type Props = {
  categoryName: string;
  validationErrors: Partial<Record<string, string>>;
  rows: ComplianceDocumentUploadRow[];
};

const shortenFileName = (value: string, maxLength = 36) => {
  if (value.length <= maxLength) return value;

  const extensionIndex = value.lastIndexOf('.');
  const hasExtension = extensionIndex > 0 && extensionIndex >= value.length - 8;
  const extension = hasExtension ? value.slice(extensionIndex) : '';
  const baseName = hasExtension ? value.slice(0, extensionIndex) : value;
  const availableBaseLength = Math.max(8, maxLength - extension.length - 1);

  return `${baseName.slice(0, availableBaseLength)}...${extension}`;
};

export function AddProductStepComplianceDocuments({
  categoryName,
  validationErrors,
  rows,
}: Props) {
  const validationMessages = Object.values(validationErrors).filter(Boolean);

  return (
    <Row className="g-4">
      {validationMessages.length > 0 && (
        <Col xs={12}>
          <Alert variant="danger">
            <ul className="mb-0 ps-3">
              {validationMessages.map((message) => (
                <li key={message}>{message}</li>
              ))}
            </ul>
          </Alert>
        </Col>
      )}

      <Col xs={12}>
        <div className="mb-3">
          <h5 className="mb-1">Compliance Documents</h5>
          <div className="text-muted">
            Upload the category-specific documents required for{' '}
            {categoryName || 'this product'}.
          </div>
        </div>

        <div className="d-flex flex-column gap-3">
          {rows.map((row) => {
            const showExistingFile = Boolean(row.existingFileName);
            const showDescription = !showExistingFile;

            return (
              <div key={row.key} className="border rounded bg-light p-3">
                <div className="d-flex flex-wrap align-items-start justify-content-between gap-3 mb-2">
                  <div>
                    <div className="fw-semibold d-flex align-items-center gap-2">
                      <i className="bi bi-file-earmark-text text-muted" />
                      <span>{row.label}</span>
                    </div>
                    {showDescription && row.description && (
                      <div className="small text-muted mt-1">
                        {row.description}
                      </div>
                    )}
                    {row.helperText && (
                      <div className="small text-muted mt-1">
                        {row.helperText}
                      </div>
                    )}
                  </div>
                  <Badge
                    bg={
                      row.badgeLabel === 'Required'
                        ? 'danger'
                        : row.badgeLabel === 'Conditional'
                          ? 'warning'
                          : 'secondary'
                    }
                  >
                    {row.badgeLabel}
                  </Badge>
                </div>

                {showExistingFile ? (
                  <div className="d-flex flex-wrap align-items-center gap-2 small">
                    {row.existingFileUrl ? (
                      <a
                        href={row.existingFileUrl}
                        target="_blank"
                        rel="noreferrer"
                      >
                        {shortenFileName(
                          row.existingFileName ?? 'Uploaded file',
                        )}
                      </a>
                    ) : (
                      <span>
                        {shortenFileName(
                          row.existingFileName ?? 'Uploaded file',
                        )}
                      </span>
                    )}
                    <Button
                      size="sm"
                      variant="link text-danger"
                      className="py-0 px-1"
                      onClick={() => void row.onRemoveExisting()}
                      aria-label={`Remove ${row.label}`}
                    >
                      <i className="bi bi-x-circle-fill" />
                    </Button>
                  </div>
                ) : (
                  <>
                    <Form.Control
                      className="w-auto mw-100"
                      type="file"
                      accept={row.accept ?? '.pdf,.doc,.docx,.jpg,.jpeg,.png'}
                      isInvalid={Boolean(row.errorMessage)}
                      onChange={(event) =>
                        void row.onFileChange(
                          (event.target as HTMLInputElement).files?.[0] ?? null,
                        )
                      }
                    />
                    <Form.Control.Feedback type="invalid">
                      {row.errorMessage}
                    </Form.Control.Feedback>
                    {row.selectedFile ? (
                      <div className="small mt-2 d-flex flex-wrap align-items-center gap-2">
                        <span className="text-muted">
                          Selected: {shortenFileName(row.selectedFile.name)}
                        </span>
                        <Button
                          size="sm"
                          variant="link text-danger"
                          className="py-0 px-1"
                          onClick={() => void row.onFileChange(null)}
                          aria-label={`Clear ${row.label}`}
                        >
                          <i className="bi bi-x-circle-fill" />
                        </Button>
                      </div>
                    ) : (
                      <div className="text-muted small mt-2">
                        Upload a file to attach it to this product.
                      </div>
                    )}
                  </>
                )}
              </div>
            );
          })}
        </div>
      </Col>
    </Row>
  );
}
